import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import Input from '../components/Input';
import AccountAccessShell, {
  AccountAccessActions,
  AccountAccessErrorText,
  AccountAccessFieldStack,
  AccountAccessForm,
  AccountAccessInfoText,
  AccountAccessLabel,
  AccountAccessMeta,
  AccountAccessMetaLabel,
  AccountAccessMetaValue,
  AccountAccessPrimaryButton,
  accountAccessInputCss,
} from '../components/auth/AccountAccessShell';
import { auth } from '../lib/firebase';
import { apiFetch } from '../lib/api';
import { getPasswordPolicyError, PASSWORD_POLICY_HINT } from '../lib/passwordPolicy';
import { useAuth } from '../context/AuthProvider';

type ViewState = 'loading' | 'ready' | 'success' | 'error';
type InviteRole = 'admin' | 'agent' | 'client';

type InviteInspectResponse = {
  ok: boolean;
  email: string;
  role: InviteRole;
  recipientName: string;
  expiresAt: number;
};

type InviteCompleteResponse = {
  ok: boolean;
  email: string;
  role: InviteRole;
  customToken: string;
};

const ROLE_LABELS: Record<InviteRole, string> = {
  admin: 'Admin',
  agent: 'Agent',
  client: 'Client',
};

const PasswordInput = styled(Input)`
  ${accountAccessInputCss};
`;

function formatDate(value: number): string {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '';
  }
}

export default function PlatformInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshRole } = useAuth();
  const token = (params.get('token') ?? '').trim();

  const [state, setState] = useState<ViewState>('loading');
  const [message, setMessage] = useState('');
  const [invite, setInvite] = useState<InviteInspectResponse | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const passwordPolicyError = useMemo(
    () => getPasswordPolicyError(password),
    [password],
  );

  useEffect(() => {
    let active = true;

    if (!token) {
      setState('error');
      setMessage('This invite link is missing its token.');
      return () => {
        active = false;
      };
    }

    setState('loading');
    setMessage('');

    apiFetch<InviteInspectResponse>('/api/auth/platform-invite/inspect', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
      .then((result) => {
        if (!active) return;
        setInvite(result);
        setState('ready');
      })
      .catch((error: any) => {
        if (!active) return;
        setState('error');
        setMessage(String(error?.message ?? 'Unable to validate invite link.'));
      });

    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token || busy) return;

    if (passwordPolicyError) {
      setState('ready');
      setMessage(passwordPolicyError);
      return;
    }
    if (!confirmPassword.trim()) {
      setState('ready');
      setMessage('Please confirm your password.');
      return;
    }
    if (password !== confirmPassword) {
      setState('ready');
      setMessage('Passwords do not match.');
      return;
    }

    setBusy(true);
    setMessage('');
    try {
      const result = await apiFetch<InviteCompleteResponse>('/api/auth/platform-invite/complete', {
        method: 'POST',
        body: JSON.stringify({
          token,
          newPassword: password,
          confirmPassword,
        }),
      });

      if (auth.currentUser) {
        await signOut(auth);
      }
      await signInWithCustomToken(auth, result.customToken);
      await refreshRole();
      setState('success');
      setMessage('Your account is ready. Redirecting to the dashboard...');
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      setState('error');
      setMessage(String(error?.message ?? 'Unable to complete invite setup.'));
    } finally {
      setBusy(false);
    }
  };

  let title = 'Checking your secure link';
  let copy = 'Please wait while we validate your onboarding request.';
  let body: ReactNode = null;

  if (state === 'ready' && invite) {
    title = 'Activate your account';
    copy = 'Set your password to finish activating your CyBard access.';
    body = (
      <>
        <AccountAccessMeta>
          <AccountAccessMetaLabel>Invited account</AccountAccessMetaLabel>
          <AccountAccessMetaValue>{invite.email}</AccountAccessMetaValue>
          <AccountAccessMetaLabel>Access</AccountAccessMetaLabel>
          <AccountAccessMetaValue>{ROLE_LABELS[invite.role]}</AccountAccessMetaValue>
          <AccountAccessMetaLabel>Expires</AccountAccessMetaLabel>
          <AccountAccessMetaValue>{formatDate(invite.expiresAt)}</AccountAccessMetaValue>
        </AccountAccessMeta>
        <AccountAccessForm onSubmit={handleSubmit}>
          <AccountAccessFieldStack>
            <AccountAccessLabel htmlFor="platform-invite-password">New password</AccountAccessLabel>
            <PasswordInput
              id="platform-invite-password"
              type="password"
              autoComplete="new-password"
              placeholder={PASSWORD_POLICY_HINT}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </AccountAccessFieldStack>
          <AccountAccessFieldStack>
            <AccountAccessLabel htmlFor="platform-invite-confirm-password">
              Confirm password
            </AccountAccessLabel>
            <PasswordInput
              id="platform-invite-confirm-password"
              type="password"
              autoComplete="new-password"
              placeholder="Repeat your password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </AccountAccessFieldStack>
          {message ? <AccountAccessInfoText>{message}</AccountAccessInfoText> : null}
          {!message && passwordPolicyError && password ? (
            <AccountAccessInfoText>{passwordPolicyError}</AccountAccessInfoText>
          ) : null}
          <AccountAccessActions>
            <AccountAccessPrimaryButton type="submit" disabled={busy}>
              {busy ? 'Setting password...' : 'Activate account'}
            </AccountAccessPrimaryButton>
          </AccountAccessActions>
        </AccountAccessForm>
      </>
    );
  } else if (state === 'success') {
    title = 'Account ready';
    copy = message || 'Your invite has been completed.';
  } else if (state === 'error') {
    title = 'Invite unavailable';
    copy = 'This invite cannot be completed right now.';
    body = (
      <>
        {message ? <AccountAccessErrorText>{message}</AccountAccessErrorText> : null}
        <AccountAccessActions>
          <AccountAccessPrimaryButton type="button" onClick={() => navigate('/', { replace: true })}>
            Return home
          </AccountAccessPrimaryButton>
        </AccountAccessActions>
      </>
    );
  }

  return (
    <AccountAccessShell title={title} copy={copy}>
      {body}
    </AccountAccessShell>
  );
}
